import { Frame } from "@masonry-grid/solid-js";
import visibilityIcon from "@ms/visibility.svg?raw";
import { BrandIcon } from "@ui/BrandIcon";
import { Loader } from "@ui/Loader";
import { Pill } from "@ui/Pill";
import { Stack } from "@ui/Stack";
import { Stat } from "@ui/Stat";
import { formatNullableCount } from "@utils/formatNullableCount";
import { secondsToTime } from "@utils/time";
import { createMemo, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import type { BoardItemListItemResponse } from "@/client/types.gen";
import { useBoardDetails } from "../BoardDetailsContext";

const DEFAULT_CLIP_ASPECT_RATIO = 9 / 16;

/** Masonry slot sizes at a shared column width (9 units wide). */
const PORTRAIT_FRAME = { width: 9, height: 16 } as const;
/** Half the portrait height so two landscape tiles stack to one portrait tile. */
const LANDSCAPE_FRAME = { width: 9, height: 8 } as const;

type Props = {
	clip: BoardItemListItemResponse;
	onPlayVideo?: (clip: BoardItemListItemResponse) => void;
};

export const Clip = (props: Props) => {
	const boardDetails = useBoardDetails();
	const clip = () => props.clip;
	const isBulkSelect = createMemo(() => boardDetails.mode() === "bulk_select");
	const selectedClipIds = createMemo(() => boardDetails.selectedClipIds());
	const isSelected = createMemo(() => {
		return selectedClipIds()
			.map((selectedClip) => selectedClip.id)
			.includes(clip().id);
	});

	const thumbnailUrl = () =>
		clip().assets?.find((asset) => asset.type === "thumbnail")?.signed_url ??
		"";

	const isRendering = () => clip().status === "rendering";

	const clipDurationSeconds = () => {
		const start = clip().clipped_start ?? clip().start ?? 0;
		const end = clip().clipped_end ?? clip().end ?? 0;
		return Math.max(0, end - start);
	};

	const thumbClass = createMemo(() => {
		const classes = ["clip-grid-cell__thumb"];
		if (boardDetails.activePlayClipId() === clip().id) {
			classes.push("clip-grid-cell__thumb--playing");
		}
		if (isBulkSelect()) {
			classes.push("bulk_select");
		}
		if (isBulkSelect() && isSelected()) {
			classes.push("selected");
		}
		return classes.join(" ");
	});

	const toggleSelected = () => {
		if (isSelected()) {
			boardDetails.setSelectedClipIds(
				boardDetails
					.selectedClipIds()
					.filter((selectedClip) => selectedClip.id !== clip().id),
			);
			return;
		}
		boardDetails.setSelectedClipIds([
			...boardDetails.selectedClipIds(),
			{ id: clip().id },
		]);
	};

	const portrait = () =>
		(clip().aspect_ratio ?? DEFAULT_CLIP_ASPECT_RATIO) <= 1;
	const frameWidth = () =>
		portrait() ? PORTRAIT_FRAME.width : LANDSCAPE_FRAME.width;
	const frameHeight = () =>
		portrait() ? PORTRAIT_FRAME.height : LANDSCAPE_FRAME.height;

	const clipName = () => clip().video?.title ?? "clip";

	return (
		<Frame width={frameWidth()} height={frameHeight()}>
			{/* In bulk select the cell is a label, so that a click anywhere in it
			    reaches the checkbox the way the browser already knows how to. */}
			<Dynamic
				component={isBulkSelect() ? "label" : "div"}
				class="clip-grid-cell"
			>
				<Dynamic
					component={isBulkSelect() ? "div" : "button"}
					class={thumbClass()}
					type={isBulkSelect() ? undefined : "button"}
					aria-label={isBulkSelect() ? undefined : `Play ${clipName()}`}
					onClick={
						isBulkSelect() ? undefined : () => props.onPlayVideo?.(clip())
					}
				>
					<Show
						when={thumbnailUrl()}
						fallback={
							<div class="clip-grid-cell__thumb-placeholder">
								<Show when={isRendering()}>
									<Loader size="small" color="var(--grey-600)" />
									<span class="clip-grid-cell__thumb-placeholder-label">
										rendering
									</span>
								</Show>
							</div>
						}
					>
						{(url) => (
							<img
								class="clip-grid-cell__thumb-image"
								src={url()}
								alt=""
								loading="lazy"
								decoding="async"
							/>
						)}
					</Show>
					<Show when={!isRendering()}>
						<Pill class="video-duration" theme="dark" size="small">
							{secondsToTime(clipDurationSeconds(), "MM:SS")}
						</Pill>
					</Show>
					<Show when={!isBulkSelect() && boardDetails.showDetails()}>
						<ClipDetailsFooter clip={clip()} />
					</Show>
				</Dynamic>
				<Show when={isBulkSelect()}>
					<input
						type="checkbox"
						aria-label={`Select ${clipName()}`}
						checked={isSelected()}
						onChange={() => {
							toggleSelected();
						}}
					/>
				</Show>
			</Dynamic>
		</Frame>
	);
};

const ClipDetailsFooter = (props: { clip: BoardItemListItemResponse }) => {
	const clip = props.clip;

	return (
		<div class="clip-grid-cell__footer">
			<div class="video-title">{clip.video?.title}</div>
			<Stack
				direction="row"
				spacing="small"
				align="center"
				justify="space-between"
			>
				<Stack direction="row" spacing="small" align="center">
					<BrandIcon platform={clip?.video?.platform ?? ""} />
					<Show
						when={clip.influencer}
						fallback={
							<span class="handle">@{clip.social_account?.handle ?? ""}</span>
						}
					>
						<span class="handle">@{clip.influencer?.handle ?? ""}</span>
					</Show>
				</Stack>
				<Stat
					class="views"
					value={formatNullableCount(clip.video?.views, { notation: "compact" })}
					icon={visibilityIcon}
				/>
			</Stack>
		</div>
	);
};
